/**
 * Locale-aware access to `content/`.
 *
 * Everything the game stores is an id -- a fragment id, a relic id, a code-name
 * id, an island name as `{ pattern, slots }`, a log event plus a seed, a letter
 * body made of ids (Q12.2). Nothing stores rendered text. These loaders are the
 * only place an id becomes words, and every one of them takes the *reader's*
 * locale, which is why a German player can read an English player's letter.
 *
 * Fallback is per entry, not per file (Q12.5): a locale that has translated
 * forty of sixty fragments shows forty in its own language and twenty in
 * English, rather than failing or reverting wholesale. `en` is the source and
 * the floor; a locale that is absent entirely simply resolves to it.
 *
 * No IO happens here. A `ContentSource` is handed in already read, by the
 * server from disk or by the web build from its bundle.
 */

import { format, type FormatArgs } from '../i18n/format.ts'
import type { ReputationTier } from '../game-config.ts'
import {
  type CodeName,
  type CodeNamesFile,
  type ContentSource,
  type Fragment,
  type GuildVoiceFile,
  type IslandNamesFile,
  type LetterGrammarFile,
  type Locale,
  type LogEvent,
  type ManuscriptFile,
  type ManuscriptSpan,
  type Mood,
  type PartialLocaleContent,
  type Relic,
} from './schemas.ts'

/** Merge two id-keyed lists, preferring the locale's entry, keeping en's order. */
function mergeById<T extends { id: string }>(source: readonly T[], locale: readonly T[] | undefined) {
  if (locale === undefined) return [...source]
  const overrides = new Map(locale.map((entry) => [entry.id, entry]))
  return source.map((entry) => overrides.get(entry.id) ?? entry)
}

export type Content = {
  /** The source locale, `en`. Everything falls back to it. */
  readonly source: Locale
  /** Locales offered to players. Only `en` until release (Q12.5). */
  readonly served: readonly Locale[]
  /** Locales present in the source at all, served or not. */
  readonly present: readonly Locale[]

  /**
   * The served locale a request should be answered in. Matches on the language
   * subtag, so `de-AT` finds `de`, and falls back to the source.
   */
  resolve(locale: string | undefined): Locale

  fragments(locale: Locale): readonly Fragment[]
  fragment(locale: Locale, id: string): Fragment | undefined

  relics(locale: Locale): readonly Relic[]
  relic(locale: Locale, id: string): Relic | undefined

  codeNames(locale: Locale): CodeNamesFile
  codeName(locale: Locale, id: string): CodeName | undefined
  /** Mood weights come from the source in every locale; they are a rule, not prose. */
  moodWeights(tier: ReputationTier): Readonly<Record<Mood, number>>

  logLines(locale: Locale, event: LogEvent): readonly string[]
  /**
   * The line a move response selects. The server sends `{ event, seed }` and
   * the client picks, so locales may hold different numbers of lines and the
   * choice stays deterministic per move (Q12.2).
   */
  logLine(locale: Locale, event: LogEvent, seed: number): string | undefined

  grammar(locale: Locale): LetterGrammarFile
  /**
   * Island-name lists for rendering. A locale with `translateIslandNames: false`
   * gets the source lists, so its players read the English proper noun (Q12.4).
   */
  islandNames(locale: Locale): IslandNamesFile

  guildVoice(locale: Locale): GuildVoiceFile
  /** A `ui` string by key, unformatted. `undefined` when no locale has it. */
  ui(locale: Locale, key: string): string | undefined
  /** A `ui` string formatted through ICU in `locale`. Falls back to the key. */
  t(locale: Locale, key: string, args?: FormatArgs): string

  manuscript(locale: Locale): ManuscriptFile
  manuscriptSpans(locale: Locale): readonly ManuscriptSpan[]
}

/** Build the locale-aware view over an already-read `content/`. */
export function createContent(source: ContentSource): Content {
  const sourceLocale = source.locales.source
  const sourceBundle: PartialLocaleContent = source.byLocale[sourceLocale] ?? {}

  const required = <K extends keyof PartialLocaleContent>(key: K): NonNullable<
    PartialLocaleContent[K]
  > => {
    const value = sourceBundle[key]
    if (value === undefined) {
      throw new Error(`content/${sourceLocale}/${key}.json is missing; the source locale is complete or nothing works`)
    }
    return value as NonNullable<PartialLocaleContent[K]>
  }

  // Every source file must exist: it is the floor everything else falls to.
  const enFragments = required('fragments')
  const enIslandNames = required('island-names')
  const enLogLines = required('log-lines')
  const enCodeNames = required('code-names')
  const enGrammar = required('letter-grammar')
  const enRelics = required('relics')
  const enGuildVoice = required('guild-voice')
  const enManuscript = required('manuscript')

  const present = Object.keys(source.byLocale).sort()
  const served = Object.entries(source.locales.locales)
    .filter(([, info]) => info.served)
    .map(([tag]) => tag)
    .sort()

  const bundle = (locale: Locale): PartialLocaleContent =>
    locale === sourceLocale ? sourceBundle : (source.byLocale[locale] ?? {})

  // Merged views are built once per locale and kept: content never changes at
  // runtime, and the letter grammar in particular is walked on every render.
  const cache = new Map<string, unknown>()
  const memo = <T>(locale: Locale, key: string, build: () => T): T => {
    const cacheKey = `${locale} ${key}`
    const hit = cache.get(cacheKey)
    if (hit !== undefined) return hit as T
    const built = build()
    cache.set(cacheKey, built)
    return built
  }

  const fragments = (locale: Locale) =>
    memo(locale, 'fragments', () =>
      mergeById(enFragments.fragments, bundle(locale).fragments?.fragments),
    )

  const relics = (locale: Locale) =>
    memo(locale, 'relics', () => mergeById(enRelics.relics, bundle(locale).relics?.relics))

  const codeNames = (locale: Locale): CodeNamesFile =>
    memo(locale, 'code-names', () => ({
      // Moods and weights are rules, identical in every locale by contract.
      moods: enCodeNames.moods,
      tierWeights: enCodeNames.tierWeights,
      names: mergeById(enCodeNames.names, bundle(locale)['code-names']?.names),
    }))

  const grammar = (locale: Locale): LetterGrammarFile =>
    memo(locale, 'letter-grammar', () => {
      const local = bundle(locale)['letter-grammar']
      if (local === undefined) return enGrammar
      const slots = Object.fromEntries(
        Object.entries(enGrammar.slots).map(([type, values]) => [
          type,
          mergeById(values, local.slots[type as keyof LetterGrammarFile['slots']]),
        ]),
      ) as LetterGrammarFile['slots']
      return {
        openings: mergeById(enGrammar.openings, local.openings),
        closings: mergeById(enGrammar.closings, local.closings),
        slots,
        categories: mergeById(enGrammar.categories, local.categories),
        // A template and its slot map travel together: taking the text of one
        // locale with the slot map of another would leave a slot unfilled.
        statements: mergeById(enGrammar.statements, local.statements),
        presets: mergeById(enGrammar.presets, local.presets),
      }
    })

  const guildVoice = (locale: Locale): GuildVoiceFile =>
    memo(locale, 'guild-voice', () => {
      const local = bundle(locale)['guild-voice']
      if (local === undefined) return enGuildVoice
      return {
        welcome: local.welcome,
        risen: local.risen,
        sinking: local.sinking,
        codeOfConduct: local.codeOfConduct,
        circulars: { ...enGuildVoice.circulars, ...local.circulars },
        ui: { ...enGuildVoice.ui, ...local.ui },
      }
    })

  const manuscript = (locale: Locale): ManuscriptFile =>
    memo(locale, 'manuscript', () => {
      const local = bundle(locale).manuscript
      if (local === undefined) return enManuscript
      return {
        title: local.title,
        spans: mergeById(enManuscript.spans, local.spans),
      }
    })

  const islandNames = (locale: Locale): IslandNamesFile => {
    const info = source.locales.locales[locale]
    if (info !== undefined && !info.translateIslandNames) return enIslandNames
    return bundle(locale)['island-names'] ?? enIslandNames
  }

  const logLines = (locale: Locale, event: LogEvent): readonly string[] => {
    const local = bundle(locale)['log-lines']?.events[event]
    if (local !== undefined && local.length > 0) return local
    return enLogLines.events[event] ?? []
  }

  return {
    source: sourceLocale,
    served,
    present,

    resolve(locale) {
      if (locale === undefined) return sourceLocale
      if (served.includes(locale)) return locale
      const language = locale.split('-')[0]
      if (language !== undefined && served.includes(language)) return language
      return sourceLocale
    },

    fragments,
    fragment: (locale, id) => fragments(locale).find((entry) => entry.id === id),

    relics,
    relic: (locale, id) => relics(locale).find((entry) => entry.id === id),

    codeNames,
    codeName: (locale, id) => codeNames(locale).names.find((entry) => entry.id === id),
    moodWeights: (tier) => {
      const row = enCodeNames.tierWeights[tier]
      if (row === undefined) throw new Error(`code-names.json has no tierWeights row for ${tier}`)
      return row
    },

    logLines,
    logLine: (locale, event, seed) => {
      const lines = logLines(locale, event)
      if (lines.length === 0) return undefined
      // A non-negative modulus, so a seed from anywhere still lands in range.
      return lines[((seed % lines.length) + lines.length) % lines.length]
    },

    grammar,
    islandNames,

    guildVoice,
    ui: (locale, key) => guildVoice(locale).ui[key],
    t: (locale, key, args) => {
      const message = guildVoice(locale).ui[key]
      // A missing key shows the key: loud in development, harmless in play.
      return message === undefined ? key : format(locale, message, args)
    },

    manuscript,
    manuscriptSpans: (locale) =>
      [...manuscript(locale).spans].sort((a, b) => a.order - b.order),
  }
}
